import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Chip,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Menu,
  MenuItem,
  Tooltip,
  Collapse,
  Alert,
} from '@mui/material';
import {
  Close,
  Send,
  PersonAdd,
  CheckCircle,
  Lock,
  Undo,
  FlashOn,
  AutoAwesome,
  Translate,
  Refresh,
  Settings,
  DirectionsBike,
} from '@mui/icons-material';
import { TripDetailsModal } from '../TripDetailsModal';
import { selectAuth } from '../../redux/slices/authSlice';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import {
  subscribeToTicket,
  sendSupportMessage,
  assignTicket,
  unassignTicket,
  updateTicketStatus,
  updateTicketPriority,
} from '../../services/ticketService';
import {
  generateTicketReply,
  translateReply,
  hasApiKey,
  AiSuggestion,
} from '../../services/aiService';
import {
  fetchLastTrip,
  LastTripInfo,
  formatTripDuration,
  formatMileage,
} from '../../services/tripService';
import AiMemoryModal from '../AiMemoryModal/AiMemoryModal';
import {
  Ticket,
  TicketMessage,
  TicketStatus,
  TicketPriority,
  CATEGORY_LABELS,
  CATEGORY_ICONS,
  STATUS_LABELS,
  STATUS_COLORS,
  PRIORITY_LABELS,
  PRIORITY_COLORS,
  QUICK_REPLIES,
} from '../../resources/types/ticketTypes';

interface Props {
  open: boolean;
  ticketId: string | null;
  onClose: () => void;
}

const TicketChatModal = ({ open, ticketId, onClose }: Props) => {
  const theme = useTheme();
  const auth = useSelector(selectAuth);
  const admin = useSelector(selectAdmins);
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [priorityMenuAnchor, setPriorityMenuAnchor] = useState<null | HTMLElement>(null);

  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiSuggestion, setAiSuggestion] = useState<AiSuggestion | null>(null);
  const [editedUkrainianReply, setEditedUkrainianReply] = useState('');
  const [translating, setTranslating] = useState(false);
  const [translatedPreview, setTranslatedPreview] = useState('');
  const [memoryModalOpen, setMemoryModalOpen] = useState(false);
  const [lastTrip, setLastTrip] = useState<LastTripInfo | null>(null);
  const [tripLoading, setTripLoading] = useState(false);
  const [tripDetailsOpen, setTripDetailsOpen] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const unsubRef = useRef<(() => void) | null>(null);

  const adminInfo = admin.info as any;
  const operatorId = adminInfo?._id || adminInfo?.login || 'admin';

  useEffect(() => {
    if (!open || !ticketId) {
      setTicket(null);
      setLoading(true);
      setError(false);
      setAiPanelOpen(false);
      setAiSuggestion(null);
      setEditedUkrainianReply('');
      setTranslatedPreview('');
      setAiError(null);
      setLastTrip(null);

      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
      return;
    }

    setLoading(true);
    setError(false);

    unsubRef.current = subscribeToTicket(ticketId, (ticketData, _messages) => {
      if (!ticketData) {
        setError(true);
        setLoading(false);
        return;
      }
      setTicket(ticketData);
      setLoading(false);
      setError(false);
    });

    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [open, ticketId]);

  useEffect(() => {
    if (!ticket?.userId) return;
    setTripLoading(true);
    fetchLastTrip(ticket.userId)
      .then((trip) => setLastTrip(trip))
      .finally(() => setTripLoading(false));
  }, [ticket?.userId]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ticket?.messages?.length]);

  const handleSend = useCallback(
    async (text?: string) => {
      const messageText = text || newMessage.trim();
      if (!messageText || sending || !ticketId) return;

      setSending(true);
      try {
        await sendSupportMessage(ticketId, messageText, operatorId);
        setNewMessage('');
        setShowQuickReplies(false);
        setTranslatedPreview('');
      } catch (err) {
        console.error('Send error:', err);
      } finally {
        setSending(false);
      }
    },
    [newMessage, sending, ticketId, operatorId]
  );

  const handleAssignToMe = useCallback(async () => {
    if (!ticketId) return;
    try {
      await assignTicket(ticketId, operatorId);
    } catch (err) {
      console.error('Assign error:', err);
    }
  }, [ticketId, operatorId]);

  const handleUnassign = useCallback(async () => {
    if (!ticketId) return;
    try {
      await unassignTicket(ticketId);
    } catch (err) {
      console.error('Unassign error:', err);
    }
  }, [ticketId]);

  const handleStatusChange = useCallback(
    async (status: TicketStatus) => {
      if (!ticketId) return;
      try {
        await updateTicketStatus(ticketId, status);
      } catch (err) {
        console.error('Status change error:', err);
      }
      setStatusMenuAnchor(null);
    },
    [ticketId]
  );

  const handlePriorityChange = useCallback(
    async (priority: TicketPriority) => {
      if (!ticketId) return;
      try {
        await updateTicketPriority(ticketId, priority);
      } catch (err) {
        console.error('Priority change error:', err);
      }
      setPriorityMenuAnchor(null);
    },
    [ticketId]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleGenerateAi = useCallback(async () => {
    if (!ticket?.messages?.length) return;
    setAiLoading(true);
    setAiError(null);
    setTranslatedPreview('');
    try {
      const suggestion = await generateTicketReply(ticket.messages, ticket.subject, lastTrip);
      setAiSuggestion(suggestion);
      setEditedUkrainianReply(suggestion.ukrainianReply);
    } catch (err: any) {
      if (err.message === 'NO_KEY') {
        setAiError('Додай REACT_APP_OPENAI_API_KEY у Replit Secrets');
      } else {
        setAiError(err.message || 'Помилка AI');
      }
    } finally {
      setAiLoading(false);
    }
  }, [ticket, lastTrip]);

  const handleToggleAiPanel = useCallback(() => {
    const next = !aiPanelOpen;
    setAiPanelOpen(next);
    if (next && !aiSuggestion && !aiLoading) {
      handleGenerateAi();
    }
  }, [aiPanelOpen, aiSuggestion, aiLoading, handleGenerateAi]);

  const handleTranslateAndInsert = useCallback(async () => {
    if (!aiSuggestion || !editedUkrainianReply.trim()) return;
    setTranslating(true);
    setAiError(null);
    try {
      const translated = await translateReply(editedUkrainianReply, aiSuggestion.detectedLanguage);
      setTranslatedPreview(translated);
      setNewMessage(translated);
    } catch (err: any) {
      setAiError(err.message || 'Помилка перекладу');
    } finally {
      setTranslating(false);
    }
  }, [aiSuggestion, editedUkrainianReply]);

  if (!open) return null;

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress sx={{ color: '#4ECDC4' }} />
        </Box>
      </Dialog>
    );
  }

  if (error || !ticket) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: 400,
            gap: 2,
            px: 3,
          }}
        >
          <Typography color="error">Не вдалося завантажити тікет</Typography>
          <Button onClick={onClose} variant="outlined" sx={{ textTransform: 'none' }}>
            Закрити
          </Button>
        </Box>
      </Dialog>
    );
  }

  const isDark = theme.palette.mode === 'dark';
  const messages: TicketMessage[] = ticket.messages || [];
  const aiKeyMissing = !hasApiKey();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { height: '90vh' } }}>
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1 }}
      >
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body2" color="textSecondary">
              {CATEGORY_ICONS[ticket.category]} {CATEGORY_LABELS[ticket.category]}
            </Typography>
            <Chip
              label={STATUS_LABELS[ticket.status]}
              size="small"
              onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
              sx={{
                backgroundColor: `${STATUS_COLORS[ticket.status]}20`,
                color: STATUS_COLORS[ticket.status],
                fontWeight: 600,
                fontSize: 11,
                cursor: 'pointer',
              }}
            />
            <Chip
              label={PRIORITY_LABELS[ticket.priority]}
              size="small"
              onClick={(e) => setPriorityMenuAnchor(e.currentTarget)}
              sx={{
                backgroundColor: `${PRIORITY_COLORS[ticket.priority]}20`,
                color: PRIORITY_COLORS[ticket.priority],
                fontWeight: 600,
                fontSize: 11,
                cursor: 'pointer',
              }}
            />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {ticket.subject}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            ID: {ticket.userId}
            {ticket.metadata?.scooterId ? ` | Самокат: ${ticket.metadata.scooterId}` : ''}
            {ticket.assignedTo ? ` | Оператор: ${ticket.assignedTo}` : ''}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3, pb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {!ticket.assignedTo && (
          <Button
            size="small"
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={handleAssignToMe}
            sx={{
              backgroundColor: '#4ECDC4',
              '&:hover': { backgroundColor: '#3BA99F' },
              textTransform: 'none',
            }}
          >
            Взяти в роботу
          </Button>
        )}
        {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<CheckCircle />}
            onClick={() => handleStatusChange('resolved')}
            sx={{ borderColor: '#4CAF50', color: '#4CAF50', textTransform: 'none' }}
          >
            Вирішено
          </Button>
        )}
        {ticket.status === 'resolved' && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<Lock />}
            onClick={() => handleStatusChange('closed')}
            sx={{ textTransform: 'none' }}
          >
            Закрити
          </Button>
        )}
        {(ticket.status === 'resolved' || ticket.status === 'closed') && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<Undo />}
            onClick={() => handleStatusChange('open')}
            sx={{ textTransform: 'none' }}
          >
            Відкрити знову
          </Button>
        )}
        {ticket.assignedTo && (
          <Button
            size="small"
            variant="outlined"
            color="warning"
            onClick={handleUnassign}
            sx={{ textTransform: 'none' }}
          >
            Повернути в чергу
          </Button>
        )}
      </Box>

      {(tripLoading || lastTrip) && (
        <Box
          sx={{
            mx: 3,
            mb: 1.5,
            p: 1.5,
            borderRadius: 2,
            border: `1px solid ${isDark ? 'rgba(78,205,196,0.25)' : 'rgba(78,205,196,0.4)'}`,
            backgroundColor: isDark ? 'rgba(78,205,196,0.06)' : 'rgba(78,205,196,0.06)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          {tripLoading ? (
            <>
              <CircularProgress size={14} sx={{ color: '#4ECDC4' }} />
              <Typography variant="caption" color="textSecondary">
                Завантаження даних поїздки...
              </Typography>
            </>
          ) : lastTrip ? (
            <>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#4ECDC4', mr: 0.5 }}>
                🛴 Остання поїздка
              </Typography>
              <Chip
                label={lastTrip.scooterName}
                size="small"
                sx={{ height: 20, fontSize: 11, backgroundColor: isDark ? '#2A2A2A' : '#F0F0F0' }}
              />
              <Typography variant="caption" color="textSecondary">
                📅{' '}
                {new Date(lastTrip.createdAt).toLocaleDateString('uk-UA', {
                  day: '2-digit',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                ⏱ {formatTripDuration(lastTrip.periodSeconds)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                📏 {formatMileage(lastTrip.mileageMeters)}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                💰 {lastTrip.sum}
              </Typography>
              {lastTrip.batteryStart !== undefined && lastTrip.batteryEnd !== undefined && (
                <Typography variant="caption" color="textSecondary">
                  🔋 {lastTrip.batteryStart}%→{lastTrip.batteryEnd}%
                </Typography>
              )}
              {lastTrip.cityName && (
                <Typography variant="caption" color="textSecondary">
                  📍 {lastTrip.cityName}
                </Typography>
              )}
              {lastTrip.active && (
                <Chip
                  label="Активна"
                  size="small"
                  sx={{ height: 18, fontSize: 10, backgroundColor: '#4CAF5020', color: '#4CAF50' }}
                />
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={<DirectionsBike sx={{ fontSize: 14 }} />}
                onClick={() => setTripDetailsOpen(true)}
                sx={{
                  ml: 'auto',
                  height: 24,
                  fontSize: 11,
                  textTransform: 'none',
                  borderColor: '#4ECDC4',
                  color: '#4ECDC4',
                  borderRadius: '6px',
                  px: 1,
                  '&:hover': {
                    borderColor: '#3BA99F',
                    color: '#3BA99F',
                    backgroundColor: 'rgba(78,205,196,0.08)',
                  },
                }}
              >
                Деталі поїздки
              </Button>
            </>
          ) : null}
        </Box>
      )}

      <Divider />

      <DialogContent
        sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden' }}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 3,
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            backgroundColor: isDark ? '#1A1A1A' : '#F5F5F5',
          }}
        >
          {messages.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography color="textSecondary" variant="body2">
                Повідомлень ще немає
              </Typography>
            </Box>
          )}
          {messages.map((msg, index) => {
            const isAdmin = msg.sender === 'admin';
            return (
              <Box
                key={msg.id || index}
                sx={{
                  alignSelf: isAdmin ? 'flex-end' : 'flex-start',
                  maxWidth: '75%',
                  backgroundColor: isAdmin ? '#4ECDC4' : isDark ? '#2A2A2A' : '#FFF',
                  color: isAdmin ? '#FFF' : 'inherit',
                  borderRadius: isAdmin ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  p: 2,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: 0.5,
                    color: isAdmin ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                  }}
                >
                  {isAdmin ? 'Підтримка' : 'Користувач'}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {msg.text}
                </Typography>
                {msg.attachments && msg.attachments.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {msg.attachments.map((url, i) => (
                      <Box
                        key={i}
                        component="a"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'block', maxWidth: 200, borderRadius: 1, overflow: 'hidden' }}
                      >
                        <Box
                          component="img"
                          src={url}
                          alt="attachment"
                          sx={{ width: '100%', height: 'auto', display: 'block' }}
                          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 0.5,
                    textAlign: 'right',
                    color: isAdmin ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                  }}
                >
                  {new Date(msg.createdAt).toLocaleString('uk-UA')}
                </Typography>
              </Box>
            );
          })}
          <div ref={messagesEndRef} />
        </Box>

        {showQuickReplies && (
          <Box
            sx={{
              px: 2,
              py: 1,
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            {QUICK_REPLIES.map((qr, i) => (
              <Chip
                key={i}
                label={qr.label}
                onClick={() => handleSend(qr.text)}
                variant="outlined"
                size="small"
                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(78, 205, 196, 0.1)' } }}
              />
            ))}
          </Box>
        )}

        <Collapse in={aiPanelOpen}>
          <Box
            sx={{
              borderTop: `2px solid #4ECDC4`,
              backgroundColor: isDark ? '#1A2A2A' : '#F0FAFA',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoAwesome sx={{ fontSize: 18, color: '#4ECDC4' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#4ECDC4' }}>
                  AI Асистент
                </Typography>
                {aiSuggestion && (
                  <Chip
                    label={aiSuggestion.detectedLanguage}
                    size="small"
                    sx={{
                      fontSize: 10,
                      height: 20,
                      backgroundColor: 'rgba(78,205,196,0.15)',
                      color: '#4ECDC4',
                    }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title="Пам'ять AI (правила та контекст)">
                  <IconButton
                    size="small"
                    onClick={() => setMemoryModalOpen(true)}
                    sx={{ color: 'text.secondary', '&:hover': { color: '#4ECDC4' } }}
                  >
                    <Settings fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Перегенерувати">
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleGenerateAi}
                      disabled={aiLoading || !messages.length}
                      sx={{ color: '#4ECDC4' }}
                    >
                      {aiLoading ? (
                        <CircularProgress size={16} sx={{ color: '#4ECDC4' }} />
                      ) : (
                        <Refresh fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            {aiError && (
              <Alert severity="warning" sx={{ py: 0 }}>
                {aiError}
              </Alert>
            )}

            {aiLoading && !aiSuggestion && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <CircularProgress size={16} sx={{ color: '#4ECDC4' }} />
                <Typography variant="body2" color="textSecondary">
                  Генерую відповідь...
                </Typography>
              </Box>
            )}

            {aiSuggestion && (
              <>
                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ mb: 0.5, display: 'block' }}
                  >
                    ✏️ Відповідь українською (для редагування)
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    value={editedUkrainianReply}
                    onChange={(e) => {
                      setEditedUkrainianReply(e.target.value);
                      setTranslatedPreview('');
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: 13,
                        '& fieldset': { borderColor: 'rgba(78,205,196,0.4)' },
                        '&:hover fieldset': { borderColor: '#4ECDC4' },
                        '&.Mui-focused fieldset': { borderColor: '#4ECDC4' },
                      },
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={
                      translating ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <Translate />
                      )
                    }
                    onClick={handleTranslateAndInsert}
                    disabled={translating || !editedUkrainianReply.trim()}
                    sx={{
                      backgroundColor: '#4ECDC4',
                      '&:hover': { backgroundColor: '#3BA99F' },
                      textTransform: 'none',
                      fontSize: 12,
                    }}
                  >
                    {translating
                      ? 'Перекладаю...'
                      : `Перекласти на ${aiSuggestion.detectedLanguage} і вставити`}
                  </Button>
                  {translatedPreview && (
                    <Typography variant="caption" sx={{ color: '#4CAF50', fontWeight: 600 }}>
                      ✓ Вставлено в поле вводу
                    </Typography>
                  )}
                </Box>

                {translatedPreview && (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      border: `1px solid rgba(78,205,196,0.3)`,
                      backgroundColor: isDark ? '#0D1F1F' : '#E8F8F7',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ display: 'block', mb: 0.5 }}
                    >
                      🌐 Переклад ({aiSuggestion.detectedLanguage}) — вставлено в поле нижче:
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
                      {translatedPreview}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Collapse>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1,
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Tooltip title="Швидкі відповіді">
            <IconButton
              onClick={() => setShowQuickReplies(!showQuickReplies)}
              size="small"
              sx={{ color: showQuickReplies ? '#4ECDC4' : 'inherit' }}
            >
              <FlashOn />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              aiKeyMissing ? 'Додай REACT_APP_OPENAI_API_KEY у Secrets' : 'AI Асистент'
            }
          >
            <span>
              <IconButton
                onClick={handleToggleAiPanel}
                size="small"
                disabled={!messages.length}
                sx={{
                  color: aiPanelOpen ? '#4ECDC4' : aiKeyMissing ? 'text.disabled' : 'inherit',
                  transition: 'color 0.2s',
                }}
              >
                <AutoAwesome />
              </IconButton>
            </span>
          </Tooltip>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder="Введіть відповідь..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sending}
          />
          <IconButton
            onClick={() => handleSend()}
            disabled={!newMessage.trim() || sending}
            sx={{
              backgroundColor: '#4ECDC4',
              color: '#FFF',
              '&:hover': { backgroundColor: '#3BA99F' },
              '&.Mui-disabled': { backgroundColor: theme.palette.action.disabledBackground },
            }}
          >
            {sending ? <CircularProgress size={20} color="inherit" /> : <Send />}
          </IconButton>
        </Box>
      </DialogContent>

      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
      >
        {(['open', 'in_progress', 'resolved', 'closed'] as TicketStatus[]).map((status) => (
          <MenuItem
            key={status}
            onClick={() => handleStatusChange(status)}
            selected={ticket.status === status}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: STATUS_COLORS[status],
                mr: 1,
              }}
            />
            {STATUS_LABELS[status]}
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={priorityMenuAnchor}
        open={Boolean(priorityMenuAnchor)}
        onClose={() => setPriorityMenuAnchor(null)}
      >
        {(['high', 'medium', 'low'] as TicketPriority[]).map((priority) => (
          <MenuItem
            key={priority}
            onClick={() => handlePriorityChange(priority)}
            selected={ticket.priority === priority}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: PRIORITY_COLORS[priority],
                mr: 1,
              }}
            />
            {PRIORITY_LABELS[priority]}
          </MenuItem>
        ))}
      </Menu>

      <AiMemoryModal open={memoryModalOpen} onClose={() => setMemoryModalOpen(false)} />

      {lastTrip && (
        <TripDetailsModal
          open={tripDetailsOpen}
          tripId={lastTrip.id}
          tripData={lastTrip.rawContract}
          onClose={() => setTripDetailsOpen(false)}
        />
      )}
    </Dialog>
  );
};

export default TicketChatModal;
